import React, {useEffect, useState, useMemo} from 'react';
import { useLocation } from 'react-router-dom';
import Table from '../Table/Table';
import { getCanteenReport } from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Checkbox, Box } from '@mui/material';

const DailyArticles = () => {
  const { t } = useTranslation('global');

  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [showData, setShowData] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const [showIngredientsByArticle, setShowIngredientsByArticle] = useState(false);
  const location = useLocation();
  const { startDate, endDate, selectedOption } = location.state || {};

  useEffect(() => {
    const retreiveInfo = async () => {
        const response = await getCanteenReport(startDate, endDate, selectedOption);
        setArticles(response);
        setLoading(false);
    };

    retreiveInfo();
  }, []);

  const column = [
    {heading: t("reports.serial"), value: "serial_num"},
    {heading: t("reports.name"), value: "employee_name"},
    {heading: t("reports.surname"), value: "last_name"},
    {heading: t("reports.organization_unit"), value: "organization_unit_name"},
    {heading: t("reports.date"), value: "datetime"},
    {heading: t("reports.transaction"), value: "transaction"},
    {heading: t("reports.article_id"), value: "article_id"},
    {heading: t("reports.article_name"), value: "article_name"},
    {heading: t("reports.amount"), value: "amount"},
    {heading: t("reports.unit_price"), value: "unit_price"},
    {heading: t("reports.price"), value: "total_price"},
  ];

  const filteredColumns = column.filter(col => {
    if (col.value === 'article_id' || col.value === 'article_name' || col.value === 'amount' || col.value === 'unit_price') {
      return showData;
    }
    return true;
  });

  // Aggregate ingredients data
  const ingredientsData = useMemo(() => {
    const ingredientMap = new Map();
    
    articles.forEach(article => {
      if (article.ingredients_details) {
        article.ingredients_details.forEach(ing => {
          const existing = ingredientMap.get(ing.id) || {
            id: ing.id,
            name: ing.name,
            unit: ing.unit,
            amount_used: 0,
            unit_price: ing.unit_price,
            total_cost: 0
          };
          
          existing.amount_used += parseFloat(ing.amount_used);
          existing.total_cost += parseFloat(ing.total_cost);
          ingredientMap.set(ing.id, existing);
        });
      }
    });
    
    return Array.from(ingredientMap.values());
  }, [articles]);

  const ingredientsByArticleData = useMemo(() => {
    const resultMap = new Map();
    
    articles.forEach(article => {
      if (article.ingredients_details) {
        article.ingredients_details.forEach(ing => {
          const key = `${article.article_id}_${ing.id}`;
          const existing = resultMap.get(key) || {
            article_id: article.article_id,
            article_name: article.article_name,
            ingredient_id: ing.id,
            ingredient_name: ing.name,
            unit: ing.unit,
            amount_used: 0,
            unit_price: ing.unit_price,
            total_cost: 0
          };
          
          existing.amount_used += parseFloat(ing.amount_used);
          existing.total_cost += parseFloat(ing.total_cost);
          resultMap.set(key, existing);
        });
      }
    });
    
    return Array.from(resultMap.values());
  }, [articles]);

  const ingredientsColumns = [
    {heading: t("reports.ingredient_id"), value: "id"},
    {heading: t("reports.ingredient_name"), value: "name"},
    {heading: t("reports.unit"), value: "unit"},
    {heading: t("reports.amount_used"), value: "amount_used"},
    {heading: t("reports.unit_price"), value: "unit_price"},
    {heading: t("reports.total_cost"), value: "total_cost"},
  ];

  const ingredientsByArticleColumns = [
    {heading: t("reports.article_id"), value: "article_id"},
    {heading: t("reports.article_name"), value: "article_name"},
    {heading: t("reports.ingredient_id"), value: "ingredient_id"},
    {heading: t("reports.ingredient_name"), value: "ingredient_name"},
    {heading: t("reports.unit"), value: "unit"},
    {heading: t("reports.amount_used"), value: "amount_used"},
    {heading: t("reports.unit_price"), value: "unit_price"},
    {heading: t("reports.total_cost"), value: "total_cost"},
  ];

  return (
    <div>
      <h2>{selectedOption}</h2>
      
      <Box sx={{display: 'flex', gap: 2}}>
        <FormControlLabel
          sx={{margin: 1}}
          control={
            <Checkbox
              checked={showData && !showIngredients && !showIngredientsByArticle}
              onChange={(e) => {
                setShowData(e.target.checked);
                setShowIngredients(false);
                setShowIngredientsByArticle(false);
              }}
            />
          }
          label={t("reports.detailedData")}
        />
        <FormControlLabel
          sx={{margin: 1}}
          control={
            <Checkbox
              checked={showIngredients}
              onChange={(e) => {
                setShowIngredients(e.target.checked);
                setShowData(false);
                setShowIngredientsByArticle(false);
              }}
            />
          }
          label={t("reports.showIngredients")}
        />
        <FormControlLabel
          sx={{margin: 1}}
          control={
            <Checkbox
              checked={showIngredientsByArticle}
              onChange={(e) => {
                setShowIngredientsByArticle(e.target.checked);
                setShowData(false);
                setShowIngredients(false);
              }}
            />
          }
          label={t("reports.showIngredientsByArticle")}
        />
      </Box>
      
      {loading ? (
        <p>{t("reports.loading")}</p>
      ) : (
        <>
          {!showIngredients && !showIngredientsByArticle && <Table data={articles} column={filteredColumns}/>}
          {showIngredients && ingredientsData.length > 0 && (
              <Table data={ingredientsData} column={ingredientsColumns}/>
          )}
          {showIngredientsByArticle && ingredientsByArticleData.length > 0 && (
              <Table data={ingredientsByArticleData} column={ingredientsByArticleColumns}/>
          )}
        </>
      )}
    </div>
  )
}

export default DailyArticles;
